import type { ElementClickedEvent } from '../../../../../types';

import * as styles from './dialog.css';
import { TrackedDialog, useAnalytics, useEventScope } from '@blueshift-ui/analytics';
import ACCOUNT_ROUTES from '../../../../../constants/account-routes';
import CANCEL_REASONS from '../../../../../constants/cancel-reasons';
import Chip from '@blueshift-ui/core/dist/components/chip';
import EVENT_NAMES from '../../../../../constants/generated/event-names';
import Typography from '@blueshift-ui/theme/dist/components/typography';
import useBreakpoints from '@blueshift-ui/theme/dist/hooks/use-breakpoints';
import useForm from '@blueshift-ui/form/dist/hooks/use-form';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';

interface CancelPlanDialogProps {
  onClose?: () => void;
  open: boolean;
  subscriptionId: string;
}

function CancelPlanDialog({ subscriptionId, onClose, open = false }: CancelPlanDialogProps) {
  const analytics = useAnalytics();
  const eventScope = useEventScope();
  const { isSmallViewport } = useBreakpoints();
  const { register, handleSubmit } = useForm();
  const { loading, translate } = useTranslation('update.membership.cancel_plan.dialog');
  const navigate = useNavigate();

  const [choice, setChoice] = useState<string | null>(null);

  function handleClick(data: any) {
    analytics.track(EVENT_NAMES.elementClicked, {
      element_id: `cancel_membership_plan_dialog_${data}_option_item`,
      element_text: translate(`reason_${data}`),
      element_type: 'button',
      metadata: {
        reason: data,
      },
      page_path: window.location.pathname,
      page_section: eventScope?.pageSection,
    } satisfies ElementClickedEvent);

    setChoice(data);
    navigate(`/${ACCOUNT_ROUTES.membership}/${subscriptionId}/cancel`, {
      state: { cancellationReason: data },
    });
  }

  if (loading) {
    return null;
  }

  return (
    <TrackedDialog
      dialogContentProps={{ className: styles.content }}
      fullScreen={isSmallViewport}
      id="cancel_membership_plan_dialog"
      maxWidth="md"
      onClose={onClose}
      open={open}
      title={
        <Typography className={styles.title} variant="h6">
          {translate('title')}
        </Typography>
      }
      withCloseButton={true}
    >
      <Typography variant="subtitle2">{translate('subtitle')}</Typography>
      <form className={styles.form} onSubmit={handleSubmit(handleClick)}>
        {CANCEL_REASONS.map((reason) => (
          <Chip
            {...register('reason', { required: true })}
            className={styles.chip}
            clickable
            key={reason}
            label={translate(`reason_${reason}`)}
            onClick={() => handleClick(reason)}
            variant={choice === reason ? 'filled' : 'outlined'}
          />
        ))}
      </form>
    </TrackedDialog>
  );
}

export default CancelPlanDialog;
